import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import CloseIcon from "@mui/icons-material/Close";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { toast } from "react-toastify";

import {
  API_split_learning_video,
  API_suggest_video_chapters,
  API_get_learning_file,
} from "../../../constants";
import { postRightworkAPI, getRightworkAPI } from "../requests/utilities/requests";
import { uploadLearningFile } from "./swipeDeckHelpers";

// Keep in sync with the backend: each clip must be within the swipe-card
// 10-min cap, and a single split produces at most MAX_CLIPS cards
// (MAX_SWIPE_CARD_VIDEO_DURATION_SECONDS / MAX_SPLIT_SEGMENTS).
const MAX_CLIP_SECONDS = 10 * 60;
const MAX_CLIPS = 20;

// Matches the form/editor modals: no padding on the Box (header/body/footer
// own their padding), soft shadow, rounded, and the whole Box scrolls with the
// header/footer stuck to it.
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 760,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

function fmtTime(t) {
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function newId() {
  return typeof window !== "undefined" && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `seg-${Date.now()}-${Math.random()}`;
}

/**
 * A small frame preview for one clip: a muted, paused <video> seeked to the
 * clip's start time. We render the frame directly (no canvas), so there's no
 * S3-CORS dependency — and an undecodable source (HEVC .mov) just shows black,
 * same as the main preview. Keyed by the clip's stable _id by the caller, so it
 * isn't reloaded on title/caption edits.
 */
function ClipThumbnail({ src, time }) {
  const ref = React.useRef(null);
  const seek = React.useCallback(() => {
    const v = ref.current;
    if (!v) return;
    try {
      v.currentTime = Math.max(0, time || 0);
    } catch (e) {
      // not seekable yet / unsupported — leave the poster frame
    }
  }, [time]);
  // Re-seek if the clip's start changes after the frame already loaded.
  React.useEffect(() => {
    const v = ref.current;
    if (v && v.readyState >= 1) seek();
  }, [seek]);
  return (
    <div className="w-24 h-16 shrink-0 rounded-md bg-black overflow-hidden ring-1 ring-slate-200">
      {src ? (
        <video
          ref={ref}
          src={src}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          onLoadedMetadata={seek}
          // object-contain so a portrait (phone) frame shows whole, pillarboxed,
          // rather than being center-cropped like object-cover would.
          className="w-full h-full object-contain pointer-events-none"
        />
      ) : null}
    </div>
  );
}

/**
 * Trim a long source video into several short swipe-card clips. The source is
 * uploaded once (skipping normalization — it may exceed the 10-min card cap and
 * never plays directly, only its cut segments do); the manager marks in/out
 * points for each clip; on confirm the backend cuts + normalizes each segment
 * into its own LearningFile and the parent appends one video card per clip
 * (each polling its own `video_status` like any single upload).
 *
 * The in-browser preview plays the un-normalized source. Two outcomes for an
 * unsupported codec (e.g. iPhone HEVC `.mov`): if the browser can still read
 * the container DURATION, the timeline is fully usable (numeric scrubbing) even
 * if frames don't paint, and the clips cut correctly server-side. If it can't
 * read the duration at all, there's no usable timeline, so we ask for an MP4.
 */
export function SwipeVideoSplitModal({ open, onClose, companyId, onCreateCards }) {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [source, setSource] = React.useState(null); // { uuid, url }
  const [duration, setDuration] = React.useState(0);
  const [sourceAspect, setSourceAspect] = React.useState(null); // width / height
  const [playhead, setPlayhead] = React.useState(0); // current video position (s)
  const [videoError, setVideoError] = React.useState(false);
  const [range, setRange] = React.useState([0, 0]); // [inSeconds, outSeconds]
  const [segments, setSegments] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null); // _id of clip loaded on the timeline (live-edited)
  const [submitting, setSubmitting] = React.useState(false);
  const [chaptering, setChaptering] = React.useState(false); // poll active (after POST commits)
  const [startingChapter, setStartingChapter] = React.useState(false); // the trigger POST is in flight

  const videoRef = React.useRef(null);
  // Out-point for the "Preview clip" auto-stop; null when not previewing.
  const stopAtRef = React.useRef(null);
  // Bumped on every new upload and on close, so a late upload resolution from a
  // superseded/closed session can't write stale state onto the modal.
  const uploadTokenRef = React.useRef(0);
  // Snapshot of the clip list at the moment chaptering started, so a successful
  // suggestion (which replaces the list) is undoable.
  const preChapterSegmentsRef = React.useRef([]);
  // The timeline track element (for pixel→time math when dragging a band) and
  // the in-progress band drag, if any.
  const trackRef = React.useRef(null);
  const dragRef = React.useRef(null);

  const reset = React.useCallback(() => {
    setSource(null);
    setDuration(0);
    setSourceAspect(null);
    setPlayhead(0);
    setVideoError(false);
    setRange([0, 0]);
    setSegments([]);
    setSelectedId(null);
    setUploadError(null);
    setUploading(false);
    setSubmitting(false);
    setChaptering(false);
    setStartingChapter(false);
    stopAtRef.current = null;
  }, []);

  // If the selected clip disappears (deleted, or replaced by AI suggestions /
  // an undo), clear the selection so the timeline handles don't point at a
  // clip that no longer exists.
  React.useEffect(() => {
    if (selectedId && !segments.some((s) => s._id === selectedId)) {
      setSelectedId(null);
    }
  }, [segments, selectedId]);

  // Always open onto a clean slate, regardless of how the prior session ended
  // (closed mid-upload, etc.).
  React.useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const handleClose = () => {
    uploadTokenRef.current += 1; // invalidate any in-flight upload
    reset();
    onClose();
  };

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const token = (uploadTokenRef.current += 1);
    setUploading(true);
    setUploadError(null);
    try {
      const { uuid, url } = await uploadLearningFile(companyId, file, {
        skipNormalization: true,
      });
      if (token !== uploadTokenRef.current) return; // superseded / closed
      setSource({ uuid, url });
    } catch (err) {
      if (token !== uploadTokenRef.current) return;
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      if (token === uploadTokenRef.current) setUploading(false);
    }
  };

  const handleSuggestChapters = async () => {
    if (!source || chaptering || startingChapter || submitting) return;
    preChapterSegmentsRef.current = segments; // snapshot for undo
    setStartingChapter(true);
    try {
      // Await the POST so the backend has committed PENDING + cleared any stale
      // suggestions BEFORE polling starts. Then every status the poll reads
      // belongs to THIS run, so we can act on a terminal state immediately —
      // no need to first observe an in-flight state (which a fast task might
      // never expose between polls, stranding the modal on "Analyzing…").
      await postRightworkAPI(API_suggest_video_chapters, {
        company_id: companyId,
        source_learning_file_uuid: source.uuid,
      });
      setChaptering(true); // arms the poll effect below
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          "Couldn't start chaptering. Please try again.",
      );
    } finally {
      setStartingChapter(false);
    }
  };

  // Manual escape from the wait (a long backlog, or a wedged job). Stops the
  // poll and re-enables the controls WITHOUT touching the backend — the queued
  // task still runs and just completes unobserved. Replaces the old wall-clock
  // backstop: it's user-initiated, so it can't auto-fire mid-wait and provoke a
  // superseding retry.
  const handleStopChaptering = () => setChaptering(false);

  // Poll the source's chaptering_status (reuses get_learning_file) once the
  // trigger POST has committed PENDING. On ready, replace the clip list with the
  // AI suggestions (undoable); on failed, stop and let the manager trim manually.
  //
  // There is intentionally NO wall-clock give-up. A chaptering task can sit
  // behind split jobs on the shared very-long queue for an unbounded time (a
  // deep backlog exceeds any fixed cap), so we poll for as long as the backend
  // reports the job alive (pending/processing). Giving up would re-enable the
  // Suggest button and invite a retry that stamps a new request_id — making the
  // still-queued task skip when it runs and re-queuing the replacement behind the
  // same backlog (a livelock). The wait ends only on a terminal status, "Stop
  // waiting", or closing the modal. The interval backs off so a long wait
  // doesn't hammer the shared backend.
  const sourceUuid = source?.uuid;
  React.useEffect(() => {
    if (!chaptering || !sourceUuid) return undefined;
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const res = await getRightworkAPI(API_get_learning_file, {
          company_id: companyId,
          learning_file_uuid: sourceUuid,
        });
        if (cancelled) return;
        const lf = res?.data?.redux?.payload?.learning_file;
        const status = lf?.chaptering_status;
        if (status === "ready") {
          const snapshot = preChapterSegmentsRef.current;
          const proposed = (lf?.chapter_suggestions_json || [])
            .map((c) => ({
              _id: newId(),
              start: Number(c.start) || 0,
              end: Number(c.end) || 0,
              title: c.title || "",
              caption: c.caption || "",
            }))
            .filter((c) => c.end > c.start);
          setChaptering(false);
          if (proposed.length) {
            setSegments(proposed); // replace current clips with the proposal
            toast.success(
              ({ closeToast }) => (
                <span>
                  Suggested {proposed.length} {proposed.length === 1 ? "clip" : "clips"} — review and edit.{" "}
                  {snapshot.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSegments(snapshot);
                        closeToast();
                      }}
                      className="underline font-semibold"
                    >
                      Undo
                    </button>
                  )}
                </span>
              ),
              { autoClose: 8000 },
            );
          } else {
            toast.info("No chapters were suggested. Trim the video manually.");
          }
          return; // terminal — stop polling
        }
        if (status === "failed") {
          setChaptering(false);
          toast.error(
            "Couldn't auto-chapter this video (it may have no speech). Trim it manually.",
          );
          return; // terminal — stop polling
        }
        // pending/processing (or a transient empty read): keep waiting.
      } catch (e) {
        // transient — keep polling
      }
      if (!cancelled) {
        // Snappy early (fast jobs finish in a few minutes), gentle once it's
        // clearly sitting in a backlog.
        const interval = Date.now() - startedAt < 2 * 60 * 1000 ? 4000 : 15000;
        timer = setTimeout(poll, interval);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [chaptering, sourceUuid, companyId]);

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    const d = v?.duration;
    if (Number.isFinite(d) && d > 0) {
      setDuration(d);
      setRange([0, Math.min(d, MAX_CLIP_SECONDS)]);
      setPlayhead(0);
    }
    // Capture the source's aspect ratio so the created cards can pick a layout:
    // a landscape clip reads best as "split" (whole frame on top, text below);
    // portrait/square fills the card as "full_bleed".
    if (v?.videoWidth && v?.videoHeight) {
      setSourceAspect(v.videoWidth / v.videoHeight);
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setPlayhead(v.currentTime); // drives the timeline playhead
    if (stopAtRef.current != null && v.currentTime >= stopAtRef.current) {
      v.pause();
      stopAtRef.current = null;
    }
  };

  const seekTo = (t) => {
    if (videoRef.current && Number.isFinite(t)) {
      videoRef.current.currentTime = t;
      setPlayhead(t); // immediate feedback while dragging a handle
    }
  };

  const handleRangeChange = (_e, val, activeThumb) => {
    if (!Array.isArray(val) || !selectedId) return;
    setRange(val);
    // Scrub the preview to whichever handle is being dragged.
    seekTo(val[activeThumb] ?? val[0]);
    // Live "auto-save": the trim writes straight through to the selected clip,
    // so switching to another clip can never drop an in-progress edit (there's
    // no uncommitted range). Mirrors how the title/caption inputs already save.
    setSegments((prev) =>
      prev.map((s) => (s._id === selectedId ? { ...s, start: val[0], end: val[1] } : s)),
    );
  };

  const previewClip = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = range[0];
    stopAtRef.current = range[1];
    v.play().catch(() => {});
  };

  const clipLength = range[1] - range[0];
  const tooLong = clipLength > MAX_CLIP_SECONDS;
  const atClipLimit = segments.length >= MAX_CLIPS;
  const canAddClip = Boolean(source) && duration > 0 && !atClipLimit;
  // The clip currently loaded on the timeline for live trimming (null = none).
  const selectedSeg = segments.find((s) => s._id === selectedId) || null;
  // Block Create on any clip the backend would reject (zero-length, or dragged
  // past the per-card cap) so the author fixes it here rather than hitting a
  // server error after submit.
  const hasInvalidClip = segments.some(
    (s) => !(s.end > s.start) || s.end - s.start > MAX_CLIP_SECONDS,
  );
  // Position (0–100) of a timestamp along the timeline track.
  const pct = (t) => (duration > 0 ? Math.max(0, Math.min(100, (t / duration) * 100)) : 0);

  // Create a new clip placed just after the last one (wrapping to the start if
  // the tail is already full) and select it, so the author positions it by
  // dragging the handles — which auto-save. There's no separate "staging range"
  // to commit first, so no Save step.
  const addClip = () => {
    if (!canAddClip) return;
    const lastEnd = segments.reduce((m, s) => Math.max(m, s.end), 0);
    const start = lastEnd < duration ? lastEnd : 0;
    let end = Math.min(
      duration,
      start + Math.min(MAX_CLIP_SECONDS, duration - start || MAX_CLIP_SECONDS),
    );
    if (!(end > start)) end = Math.min(duration, start + 1); // tiny-source guard
    const id = newId();
    setSegments((prev) => [...prev, { _id: id, start, end, title: "", caption: "" }]);
    setSelectedId(id);
    setRange([start, end]);
    seekTo(start);
  };

  const updateSeg = (i, patch) =>
    setSegments((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeSeg = (i) =>
    setSegments((prev) => prev.filter((_, idx) => idx !== i));

  // Load a clip onto the timeline for live trimming. Handle drags write through
  // immediately (handleRangeChange), so switching between clips is always safe —
  // there's nothing to save first.
  const selectClipForEdit = (seg) => {
    setSelectedId(seg._id);
    setRange([seg.start, seg.end]);
    seekTo(seg.start);
  };

  // Drag a clip's BODY (the middle of its band) to slide the whole clip earlier
  // or later in time, keeping its length. The edges are the trim handles (on top
  // via z-index), so a grab near an edge resizes; a grab in the middle moves. A
  // press that doesn't move past a small threshold is treated as a click → select
  // (so clicking still selects, dragging moves). Writes through live like the
  // handle trim, so there's nothing to save.
  const handleBandPointerDown = (e, seg) => {
    if (e.button != null && e.button !== 0) return; // primary button only
    const width = trackRef.current?.getBoundingClientRect().width || 0;
    if (!width || !duration) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* capture unsupported — drag still works while the pointer stays over */
    }
    dragRef.current = {
      id: seg._id,
      pointerId: e.pointerId,
      startX: e.clientX,
      origStart: seg.start,
      length: seg.end - seg.start,
      width,
      moved: false,
    };
  };

  const handleBandPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const deltaPx = e.clientX - d.startX;
    if (!d.moved && Math.abs(deltaPx) < 3) return; // still within click threshold
    d.moved = true;
    const deltaTime = (deltaPx / d.width) * duration;
    // Clamp so the clip stays fully inside [0, duration] without changing length.
    const newStart = Math.max(0, Math.min(duration - d.length, d.origStart + deltaTime));
    const newEnd = newStart + d.length;
    if (selectedId !== d.id) setSelectedId(d.id); // grabbing a clip selects it
    setSegments((prev) =>
      prev.map((s) => (s._id === d.id ? { ...s, start: newStart, end: newEnd } : s)),
    );
    setRange([newStart, newEnd]);
    seekTo(newStart);
  };

  const handleBandPointerUp = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* no-op */
    }
    dragRef.current = null;
    if (!d.moved) {
      // No meaningful drag — treat as a click and just select the clip.
      setSelectedId(d.id);
      setRange([d.origStart, d.origStart + d.length]);
      seekTo(d.origStart);
    }
  };

  const handleCreate = async () => {
    if (!source || segments.length === 0) return;
    setSubmitting(true);
    try {
      const res = await postRightworkAPI(API_split_learning_video, {
        company_id: companyId,
        source_learning_file_uuid: source.uuid,
        segments: JSON.stringify(
          segments.map((s) => ({
            start: s.start,
            end: s.end,
            title: s.title,
            caption: s.caption,
          })),
        ),
      });
      const created = res?.data?.redux?.payload?.segments || [];
      // Landscape source → "split" (whole frame on top, text below); portrait
      // or square → "full_bleed" (fills the card). Default to full_bleed when
      // the aspect couldn't be read.
      const layoutVariant =
        sourceAspect && sourceAspect > 1 ? "split" : "full_bleed";
      onCreateCards(created, layoutVariant);
      toast.success(
        `Creating ${created.length} ${created.length === 1 ? "card" : "cards"}…`,
        { autoClose: 3000 },
      );
      handleClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          "Couldn't split the video. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // The source loaded but we couldn't read its duration AND playback errored —
  // there's no usable timeline, so don't pretend there is.
  const unreadable = Boolean(source) && videoError && duration <= 0;
  const ready = Boolean(source) && duration > 0;

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="video-split-modal">
      <Box sx={modalStyle}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 id="video-split-modal" className="text-xl font-bold text-slate-900">
                Split a video into cards
              </h3>
              <p className="text-sm text-slate-600">
                Upload one longer video, mark the moments you want, and we'll make
                a card from each clip.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
        {/* Step 1: upload the source */}
        {!source ? (
          <div>
            <label className="flex flex-col items-center justify-center h-40 rounded-2xl border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-sm text-slate-500">
              <ContentCutIcon style={{ fontSize: 30 }} className="text-violet-500 mb-2" />
              <span className="font-medium text-slate-700">
                {uploading ? "Uploading…" : "Click to upload a video"}
              </span>
              <span className="text-[11px] text-slate-400 mt-1">
                Any phone or screen recording · up to ~45 min
              </span>
              <input
                type="file"
                accept=".mp4,.mov,.m4v,.webm"
                onChange={handlePick}
                disabled={uploading}
                className="hidden"
              />
            </label>
            {uploadError && (
              <div className="text-xs text-red-600 mt-2">{uploadError}</div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Preview (always mounted so metadata/error events fire). The
                video sizes to its own aspect (max 18rem tall) and centers, so a
                portrait phone clip sits compact in the middle instead of
                floating in a wide black field; a landscape clip fills more
                width. */}
            <div className="flex justify-center">
              <video
                ref={videoRef}
                src={source.url || undefined}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onError={() => setVideoError(true)}
                playsInline
                controls
                className="max-h-72 max-w-full rounded-xl bg-black"
              />
            </div>

            {unreadable ? (
              <>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  We can't read this video in your browser (often an iPhone HEVC
                  .mov). Please upload an MP4 (H.264), or convert it first, then
                  try again.
                </div>
              </>
            ) : !ready ? (
              <div className="text-sm text-slate-500">Loading video…</div>
            ) : (
              <>
                {/* AI auto-chaptering — proposes clips from the transcript.
                    Replaces the current clip list when it lands. */}
                <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 flex items-center justify-between gap-3">
                  <div className="text-xs text-violet-900">
                    <span className="font-semibold">Let AI find the clips.</span>{" "}
                    We'll transcribe the video and suggest a card for each topic
                    {segments.length > 0 ? " (replaces your current clips)" : ""}.
                  </div>
                  {chaptering ? (
                    <div className="shrink-0 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700">
                        <AutoAwesomeIcon style={{ fontSize: 18 }} className="animate-pulse" />
                        Analyzing…
                      </span>
                      <button
                        type="button"
                        onClick={handleStopChaptering}
                        className="text-xs font-semibold text-violet-700 underline hover:text-violet-900"
                      >
                        Stop waiting
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSuggestChapters}
                      disabled={startingChapter || submitting}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <AutoAwesomeIcon style={{ fontSize: 18 }} />
                      {startingChapter ? "Starting…" : "Suggest chapters"}
                    </button>
                  )}
                </div>

                {videoError && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                    This video can't show frames in your browser, but the
                    timeline below still works and the clips will be cut
                    correctly. Use the start/end times to mark each clip.
                  </div>
                )}

                {/* Timeline: already-clipped bands + current selection + a live
                    playhead, with the trim handles layered on top. */}
                <div>
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                    <span>{selectedSeg ? "Trimming selected clip" : "Timeline"}</span>
                    {selectedSeg && (
                      <span className="tabular-nums">
                        {fmtTime(range[0])} – {fmtTime(range[1])} ({fmtTime(clipLength)})
                      </span>
                    )}
                  </div>

                  <div className="relative h-10" ref={trackRef}>
                    {/* Track of every clip; the selected one is highlighted and
                        is the one the draggable handles below control. */}
                    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-7 rounded-md bg-slate-100 ring-1 ring-slate-200 overflow-hidden">
                      {segments.map((seg, i) => {
                        const isSel = seg._id === selectedId;
                        return (
                          <div
                            key={seg._id}
                            title={`Card ${i + 1}: ${fmtTime(seg.start)} – ${fmtTime(seg.end)}`}
                            className={[
                              "absolute inset-y-0 overflow-hidden border-x",
                              isSel
                                ? "bg-violet-600/30 border-violet-600"
                                : "bg-violet-300/60 border-violet-500",
                            ].join(" ")}
                            style={{
                              left: `${pct(seg.start)}%`,
                              width: `${Math.max(0, pct(seg.end) - pct(seg.start))}%`,
                            }}
                          >
                            <span className="absolute top-0.5 left-1 text-[9px] font-bold leading-none text-violet-800">
                              {i + 1}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Live playhead (video position) — a soft, non-interactive
                        indicator (no knob) so it reads as a position marker, not
                        a draggable control. */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-rose-400/80 pointer-events-none"
                      style={{ left: `${pct(playhead)}%` }}
                    />

                    {/* Draggable in/out handles for the SELECTED clip — rail/track
                        hidden so only the thumbs show over the custom track above.
                        Shown only when a clip is selected; dragging auto-saves. */}
                    {selectedSeg && (
                      <Slider
                        value={range}
                        onChange={handleRangeChange}
                        min={0}
                        max={duration}
                        step={0.5}
                        disableSwap
                        valueLabelDisplay="auto"
                        valueLabelFormat={fmtTime}
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#6d28d9",
                          // The slider spans the full width, so to let a click on a
                          // clip band reach its select-overlay we make the ROOT
                          // non-interactive and re-enable only the THUMBS. A press
                          // on a thumb still bubbles to the root's drag handler, so
                          // dragging works; clicks on the rail fall through to the
                          // band overlays below. The high z-index keeps the selected
                          // clip's handles grabbable even where an OVERLAPPING
                          // clip's band overlay sits on top of them.
                          pointerEvents: "none",
                          zIndex: 30,
                          "& .MuiSlider-rail": { opacity: 0 },
                          "& .MuiSlider-track": { opacity: 0, border: "none" },
                          "& .MuiSlider-thumb": {
                            pointerEvents: "auto",
                            width: 5,
                            height: 28,
                            borderRadius: "2px",
                            backgroundColor: "#6d28d9",
                            "&:hover, &.Mui-focusVisible": {
                              boxShadow: "0 0 0 6px rgba(109,40,217,0.16)",
                            },
                          },
                        }}
                      />
                    )}

                    {/* Per-clip body hit target: click to select, drag to move
                        the whole clip. Sits ABOVE the slider rail (zIndex) so a
                        band press isn't swallowed as "move the nearest trim
                        handle"; the slider THUMBS sit higher still (zIndex 30), so
                        a grab near an edge resizes while the middle moves. Covers
                        every clip, including the selected one, so its body is
                        draggable too. */}
                    {segments.map((seg, i) => (
                      <button
                        key={`pick-${seg._id}`}
                        type="button"
                        onPointerDown={(e) => handleBandPointerDown(e, seg)}
                        onPointerMove={handleBandPointerMove}
                        onPointerUp={handleBandPointerUp}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectClipForEdit(seg);
                          }
                        }}
                        aria-label={`Select Card ${i + 1}`}
                        title={`Card ${i + 1} — click to select, drag to move`}
                        className="absolute top-1/2 -translate-y-1/2 h-7 rounded-md cursor-grab active:cursor-grabbing touch-none hover:bg-violet-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        style={{
                          left: `${pct(seg.start)}%`,
                          width: `${Math.max(0, pct(seg.end) - pct(seg.start))}%`,
                          zIndex: 20,
                        }}
                      />
                    ))}
                  </div>

                  {/* Time axis */}
                  <div className="flex items-center justify-between text-[11px] tabular-nums text-slate-400 mt-1">
                    <span>0:00</span>
                    <span className="font-medium text-rose-400">{fmtTime(playhead)}</span>
                    <span>{fmtTime(duration)}</span>
                  </div>

                  {selectedSeg && tooLong && (
                    <div className="text-xs text-red-600 mt-1">
                      A clip can be at most {MAX_CLIP_SECONDS / 60} minutes. Shorten
                      the range.
                    </div>
                  )}
                  {!selectedSeg && (
                    <div className="text-xs text-slate-500 mt-1">
                      {segments.length > 0
                        ? "Click a clip below to trim it, or add a new one."
                        : "Add a clip to start, then drag its handles to trim."}
                    </div>
                  )}
                </div>

                {/* Clip actions — add a new clip (auto-selected for trimming)
                    and preview the selected clip. No Save: trims auto-save. */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={addClip}
                    disabled={!canAddClip}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 text-sm font-medium transition-colors"
                  >
                    <AddIcon style={{ fontSize: 18 }} />
                    Add clip
                  </button>
                  {selectedSeg && (
                    <button
                      type="button"
                      onClick={previewClip}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 hover:border-violet-500 hover:text-violet-700 text-slate-700 px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <PlayArrowIcon style={{ fontSize: 18 }} />
                      Preview clip
                    </button>
                  )}
                  {atClipLimit && (
                    <span className="text-xs text-slate-500">
                      {MAX_CLIPS}-clip limit reached
                    </span>
                  )}
                </div>

                {/* Segment list */}
                {segments.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      {segments.length} {segments.length === 1 ? "clip" : "clips"}
                    </div>
                    {segments.map((seg, i) => {
                      const isSelected = seg._id === selectedId;
                      return (
                      <div
                        key={seg._id}
                        className={[
                          "rounded-xl border p-3 flex gap-3 transition-colors",
                          isSelected
                            ? "border-violet-400 ring-1 ring-violet-300 bg-violet-50/50"
                            : "border-slate-200",
                        ].join(" ")}
                      >
                        {/* Thumbnail doubles as the "load this clip onto the
                            timeline" affordance (role=button, not <button>, since
                            it wraps a <video>). */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => selectClipForEdit(seg)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              selectClipForEdit(seg);
                            }
                          }}
                          title="Trim this clip"
                          className="relative group self-start shrink-0 rounded-md cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        >
                          <ClipThumbnail src={source.url} time={seg.start} />
                          <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 group-hover:bg-black/40 transition-colors">
                            <span className="text-[10px] font-semibold text-white opacity-0 group-hover:opacity-100">
                              Trim
                            </span>
                          </span>
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => selectClipForEdit(seg)}
                              className="text-sm font-semibold text-slate-700 tabular-nums text-left hover:text-violet-700"
                            >
                              Card {i + 1} · {fmtTime(seg.start)} – {fmtTime(seg.end)}
                              {isSelected && (
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                                  Editing
                                </span>
                              )}
                            </button>
                            <IconButton
                              onClick={() => removeSeg(i)}
                              aria-label="Remove clip"
                              size="small"
                              sx={{ borderRadius: "8px" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </div>
                          <input
                            type="text"
                            value={seg.title}
                            onChange={(e) => updateSeg(i, { title: e.target.value })}
                            placeholder="Title (optional)"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
                          />
                          <input
                            type="text"
                            value={seg.caption}
                            onChange={(e) => updateSeg(i, { caption: e.target.value })}
                            placeholder="Caption (optional)"
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
                          />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 z-10 rounded-b-2xl">
          <div className="flex items-center justify-end gap-3">
            {/* A clip was dragged past the per-card limit — block Create until
                it's shortened (Create stays disabled below too). */}
            {hasInvalidClip && (
              <span className="mr-auto text-xs text-red-600">
                One clip is over the {MAX_CLIP_SECONDS / 60}-minute limit. Shorten it.
              </span>
            )}
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={
                submitting ||
                chaptering ||
                startingChapter ||
                hasInvalidClip ||
                segments.length === 0
              }
              className="px-6 py-2.5 rounded-lg font-medium text-white transition-colors bg-violet-700 hover:bg-violet-800 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {submitting
                ? "Creating…"
                : segments.length
                  ? `Create ${segments.length} ${segments.length === 1 ? "card" : "cards"}`
                  : "Create cards"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default SwipeVideoSplitModal;
